import React, { useState, useRef, useEffect } from "react";
import styles from "./css/SearchInput.module.css";
import { useTranslation } from "react-i18next";
import { langDirection } from "@/utils/languages";
import magnifierActive from "../../assets/magnifierActive.svg";
import magnifierStatic from "../../assets/magnifierStatic.svg";

interface HasName {
  name: string;
  [key: string]: any;
}

interface SearchInputProps<T extends HasName> {
  disabled?: boolean;
  placeholder?: string;
  id: string;
  className?: string;
  errorMessage?: string;
  showError?: boolean;
  options: T[];
  onSelect: (option: T | null) => void;
  selectedOption?: T | null;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  size?: "full" | "";
  filterFunction?: (searchText: string, options: T[]) => T[];
}

export const SearchInput = <T extends HasName>({
  disabled = false,
  placeholder = "",
  id,
  className = "",
  errorMessage = "",
  showError = true,
  options,
  onSelect,
  selectedOption,
  onKeyDown,
  size = "",
  filterFunction,
}: SearchInputProps<T>) => {
  const [isFocused, setIsFocused] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<T[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation("LanguageSwitcher");
  const currentLanguage = i18n.language;
  const direction = langDirection(currentLanguage);

  useEffect(() => {
    // Update search text when selectedOption changes from outside
    if (selectedOption) {
      setSearchText(selectedOption.name);
    }
  }, [selectedOption]);

  useEffect(() => {
    // Filter options based on search text using custom filter function or default behavior
    const filtered = filterFunction 
      ? filterFunction(searchText, options)
      : options.filter(option =>
          option.name.toLowerCase().includes(searchText.toLowerCase())
        );
    setFilteredOptions(filtered);
  }, [searchText, options, filterFunction]);

  useEffect(() => {
    // Handle click outside to close dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFocus = () => {
    if (!disabled) {
      setIsFocused(true);
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    if (!disabled) {
      setIsFocused(false);
      // Don't hide dropdown immediately to allow click events on options
      setTimeout(() => {
        if (!dropdownRef.current?.contains(document.activeElement)) {
          setShowDropdown(false);
        }
      }, 200);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);
    setShowDropdown(true);
    if (!value) {
      onSelect(null);
    }
  };

  const handleOptionClick = (option: T) => {
    setSearchText(option.name);
    onSelect(option);
    setShowDropdown(false);
  };

  return (
    <div className={`${styles.wrapper} ${size === "full" ? styles.fullWidth : ""} relative`}>
      <div className={`${styles.fieldContainer} ${size === "full" ? styles.fullWidth : ""}`}>
        <div className={`${styles.fieldInnerContainer} ${size === "full" ? styles.fullWidth : ""}`}>
          <div
            className={`${size === "full" ? styles.fullWidth : ""} ${styles.fieldInputContainer} ${className} ${
              errorMessage ? styles.fieldInputContainerError : ""
            } ${
              disabled
                ? styles.fieldInputContainerDisabled
                : isFocused
                ? styles.fieldInputContainerActive
                : styles.fieldInputContainerDefault
            }`}
          >
            <input
              data-testid={id}
              id={id}
              ref={inputRef}
              className={`${size === "full" ? styles.fullWidth : ""} ${styles.fieldInput} ${
                disabled ? styles.fieldInputDisabled : styles.fieldInputDefault
              } ${styles[direction]}`}
              placeholder={placeholder}
              type="text"
              value={searchText}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={onKeyDown}
              disabled={disabled}
              dir={direction}
              style={{ textAlign: "right" }}
            />
            <img
          className="w-6 h-6 ml-2"
          alt="Magnifier"
          src={
            isFocused ? magnifierActive : magnifierStatic
          }
        />
          </div>
        </div>
      </div>

      {/* Modified Dropdown */}
      {showDropdown && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute z-10 mt-[-5px] bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto ${
            size === "full" ? "w-full" : "min-w-full"
          }`}
          style={{ top: "100%" }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleOptionClick(option)}
            >
              {option.name}
            </div>
          ))}
        </div>
      )}

      {showError && (
        <div className={styles.errorMessageContainer}>
          <div
            className={`${styles.errorMessage} ${errorMessage ? styles.visible : ""}`}
          >
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
};